/**
 * Per-lens competition volume attribution for Program Insights footers.
 * Powerlifting / Olympic / Strongman: `ex.primary` slug. Streetlifting: `squat` primary + movement name heuristics.
 */

/** @typedef {"powerlifting"|"olympic_weightlifting"|"strongman"|"streetlifting"} InsightsPerLiftLens */

export const INSIGHTS_PER_LIFT_LENS_IDS = Object.freeze([
  "powerlifting",
  "olympic_weightlifting",
  "strongman",
  "streetlifting",
]);

/** `ex.primary` values that count toward each lens combined volume (lowercase). */
const LENS_COMP_PRIMARY_SLUGS = Object.freeze({
  powerlifting: new Set(["squat", "bench", "deadlift"]),
  olympic_weightlifting: new Set(["clean", "jerk", "snatch", "squat"]),
  strongman: new Set(["overheadpress", "deadlift", "squat"]),
  streetlifting: new Set(["squat"]),
});

/**
 * Streetlifting movement phrases (EN + ES). Keys align with per-lift card `liftId` values.
 * Matched after `normalizeMovementNameForMatch` + optional load suffix strip.
 */
const STREETLIFTING_MOVEMENT_PHRASES = Object.freeze({
  muscleUp: [
    "weighted muscle up",
    "weighted muscle-up",
    "muscle up weighted",
    "muscle up + weight",
    "muscle up with weight",
    "muscle-up weighted",
    "weighted mu",
    "mu weighted",
    "muscle up con peso",
    "muscle up + peso",
    "muscle up lastrado",
    "muscle up con lastre",
    "muscle up pesado",
    "mu con peso",
    "mu lastrado",
  ],
  weightedDip: [
    "weighted dip",
    "dip weighted",
    "dips weighted",
    "weighted dips",
    "dip + weight",
    "dips + weight",
    "dip with weight",
    "dips with weight",
    "fondos con peso",
    "fondo con peso",
    "fondos lastrados",
    "fondo lastrado",
    "fondos + peso",
    "fondos con lastre",
    "dip con peso",
    "dips con peso",
  ],
  weightedPullUp: [
    "weighted pull up",
    "weighted pullup",
    "weighted pull-up",
    "pull up weighted",
    "pull-up weighted",
    "pullups weighted",
    "weighted pullups",
    "pull up + weight",
    "pull-up + weight",
    "pull up with weight",
    "pull-up with weight",
    "chin up weighted",
    "weighted chin up",
    "weighted chin-up",
    "dominadas con peso",
    "dominada con peso",
    "dominadas lastradas",
    "dominada lastrada",
    "dominadas + peso",
    "dominadas con lastre",
    "pull up con peso",
    "pull up lastrado",
    "chin up con peso",
    "dominada",
    "dominadas",
    "pull up",
    "pull ups",
    "pullup",
    "pullups",
  ],
});

/** Order: first match wins (squat handled via `primary` before this runs). */
const STREETLIFTING_MATCH_ORDER = Object.freeze([
  "muscleUp",
  "weightedDip",
  "weightedPullUp",
]);

/** Required competition lift ids per per-lift lens. */
export const LENS_REQUIRED_LIFT_IDS = Object.freeze({
  powerlifting: Object.freeze(["squat", "bench", "deadlift"]),
  olympic_weightlifting: Object.freeze(["clean", "jerk", "snatch", "squat"]),
  strongman: Object.freeze(["overhead", "deadlift", "squat"]),
  streetlifting: Object.freeze([
    "muscleUp",
    "weightedDip",
    "weightedPullUp",
    "squat",
  ]),
});

/** i18n keys under `programs.programInsights.panel.coverage.*` */
export const LENS_FULL_COVERAGE_VARIANT = Object.freeze({
  powerlifting: "fullSbd",
  olympic_weightlifting: "fullOlympic",
  strongman: "fullStrongman",
  streetlifting: "fullStreetlifting",
});

/**
 * Program exercise `primary` slot → Powerlifting SBD pillar (`squat` | `bench` | `deadlift`), or null.
 * @param {unknown} primary
 * @returns {string|null}
 */
function normalizePrimarySlug(primary) {
  if (primary == null || primary === "") {
    return null;
  }
  let k;
  if (typeof primary === "string") {
    k = primary.trim().toLowerCase();
  } else if (typeof primary === "number" || typeof primary === "bigint") {
    k = String(primary).trim().toLowerCase();
  } else {
    return null;
  }
  return k || null;
}

/**
 * Lowercase, strip accents, punctuation → spaces, collapse whitespace.
 * @param {unknown} raw
 * @returns {string}
 */
export function normalizeMovementNameForMatch(raw) {
  if (raw == null) {
    return "";
  }
  let s;
  if (typeof raw === "string") {
    s = raw.trim().toLowerCase();
  } else if (typeof raw === "number" || typeof raw === "bigint") {
    s = String(raw).trim().toLowerCase();
  } else {
    return "";
  }
  if (!s) {
    return "";
  }
  s = s.normalize("NFD").replace(/\p{M}/gu, "");
  s = s.replace(/[_\-–—/]+/g, " ");
  s = s.replace(/[^\p{L}\p{N}\s+]/gu, " ");
  s = s.replace(/\s+/g, " ").trim();
  return s;
}

/**
 * Removes trailing load tokens (`20kg`, `45 lb`, `10kg`) for name matching.
 * @param {string} normalized — output of `normalizeMovementNameForMatch`
 * @returns {string}
 */
export function stripTrailingLoadFromMovementName(normalized) {
  if (!normalized) {
    return "";
  }
  return normalized
    .replace(
      /\s+\d+(\.\d+)?\s*(kg|kgs|lb|lbs|#)\s*$/i,
      ""
    )
    .replace(/\s+\d+(\.\d+)?\s*$/i, "")
    .trim();
}

/**
 * Human-readable movement labels on a program exercise (order: library name first).
 * Program builder stores the coach movement name on `secondary`; `movement` is often a slug or absent.
 *
 * @param {object|null|undefined} ex — resolved program exercise.
 * @returns {string[]}
 */
export function collectExerciseMovementLabelsForInsights(ex) {
  if (ex == null) {
    return [];
  }
  /** @type {string[]} */
  const labels = [];
  const push = (value) => {
    if (typeof value === "string" && value.trim()) {
      labels.push(value.trim());
    }
  };
  push(ex.secondary);
  push(ex.customName);
  push(ex.movement);
  push(ex.exercise?.movement);
  return [...new Set(labels)];
}

/**
 * @param {object|null|undefined} ex — resolved program exercise.
 * @returns {string}
 */
export function getExerciseMovementLabelForInsights(ex) {
  const labels = collectExerciseMovementLabelsForInsights(ex);
  return labels[0] ?? "";
}

/**
 * @param {string} label
 * @returns {"muscleUp"|"weightedDip"|"weightedPullUp"|null}
 */
function mapStreetliftingMovementToLiftId(label) {
  const base = stripTrailingLoadFromMovementName(
    normalizeMovementNameForMatch(label)
  );
  if (!base) {
    return null;
  }
  if (base === "mu" || /\bmu\b/.test(base)) {
    return "muscleUp";
  }
  const haystack = ` ${base} `;
  for (const key of STREETLIFTING_MATCH_ORDER) {
    const phrases = STREETLIFTING_MOVEMENT_PHRASES[key].map((p) =>
      normalizeMovementNameForMatch(p)
    );
    for (const phrase of phrases) {
      if (!phrase) {
        continue;
      }
      if (haystack.includes(` ${phrase} `) || base === phrase) {
        return key;
      }
    }
  }
  return null;
}

/**
 * Program exercise `primary` slot → Powerlifting SBD pillar (`squat` | `bench` | `deadlift`), or null.
 * @param {unknown} primary
 * @returns {"squat"|"bench"|"deadlift"|null}
 */
export function mapExercisePrimaryToPowerliftingSbdSlot(primary) {
  const k = normalizePrimarySlug(primary);
  if (k === "squat" || k === "bench" || k === "deadlift") {
    return k;
  }
  return null;
}

/**
 * Maps a resolved exercise to a competition lift id for the active lens, or null.
 *
 * @param {object|null|undefined} ex
 * @param {InsightsPerLiftLens|string} lens
 * @returns {string|null}
 */
export function mapExerciseToLensLiftId(ex, lens) {
  if (!ex || !lens) {
    return null;
  }
  if (lens === "powerlifting") {
    return mapExercisePrimaryToPowerliftingSbdSlot(ex.primary);
  }
  if (lens === "streetlifting") {
    const slug = normalizePrimarySlug(ex.primary);
    if (slug === "squat") {
      return "squat";
    }
    for (const label of collectExerciseMovementLabelsForInsights(ex)) {
      const liftId = mapStreetliftingMovementToLiftId(label);
      if (liftId) {
        return liftId;
      }
    }
    return null;
  }
  const slug = normalizePrimarySlug(ex.primary);
  if (!slug) {
    return null;
  }
  if (lens === "strongman" && slug === "overheadpress") {
    return "overhead";
  }
  const required = LENS_REQUIRED_LIFT_IDS[lens];
  if (required?.includes(slug)) {
    return slug;
  }
  return null;
}

/**
 * Whether this exercise counts toward the active lens combined competition volume.
 *
 * @param {object|null|undefined} ex
 * @param {InsightsPerLiftLens|string} lens
 * @returns {boolean}
 */
export function exerciseCountsTowardLensCompVolume(ex, lens) {
  if (!ex || !lens) {
    return false;
  }
  const slug = normalizePrimarySlug(ex.primary);

  if (lens === "streetlifting") {
    return mapExerciseToLensLiftId(ex, lens) != null;
  }

  const allowed = LENS_COMP_PRIMARY_SLUGS[lens];
  if (!allowed) {
    return false;
  }
  return slug != null && allowed.has(slug);
}

/**
 * Sum of `volumeKg` for exercises that count toward the lens competition volume.
 *
 * @param {object[]} exercises
 * @param {InsightsPerLiftLens|string} lens
 * @param {(ex: object) => { volumeKg: number }} accumulateExerciseSetAggregates
 * @returns {number}
 */
export function computeLensCompVolumeKgForExercises(
  exercises,
  lens,
  accumulateExerciseSetAggregates
) {
  let compVolKg = 0;
  for (const ex of exercises) {
    if (!exerciseCountsTowardLensCompVolume(ex, lens)) {
      continue;
    }
    const agg = accumulateExerciseSetAggregates(ex);
    compVolKg += agg.volumeKg;
  }
  return compVolKg;
}

/**
 * All per-lift lens competition volumes for one week (single pass).
 *
 * @param {object[]} exercises
 * @param {(ex: object) => { volumeKg: number }} accumulateExerciseSetAggregates
 * @returns {Record<InsightsPerLiftLens, number>}
 */
export function computeAllLensCompVolumeKgForExercises(
  exercises,
  accumulateExerciseSetAggregates
) {
  const totals = {
    powerlifting: 0,
    olympic_weightlifting: 0,
    strongman: 0,
    streetlifting: 0,
  };
  for (const ex of exercises) {
    const agg = accumulateExerciseSetAggregates(ex);
    const vol = agg.volumeKg;
    if (vol <= 0) {
      continue;
    }
    for (const lens of INSIGHTS_PER_LIFT_LENS_IDS) {
      if (exerciseCountsTowardLensCompVolume(ex, lens)) {
        totals[lens] += vol;
      }
    }
  }
  return totals;
}

/**
 * @param {Set<string>|string[]} programmedLiftIds
 * @param {InsightsPerLiftLens|string} lens
 * @returns {{ coverageVariant: string|undefined, unprogrammedLiftIds: string[] }}
 */
export function computeLensLiftCoverageFromProgrammedLiftIds(
  programmedLiftIds,
  lens
) {
  const required = LENS_REQUIRED_LIFT_IDS[lens];
  if (!required) {
    return { coverageVariant: undefined, unprogrammedLiftIds: [] };
  }
  const programmed =
    programmedLiftIds instanceof Set
      ? programmedLiftIds
      : new Set(programmedLiftIds);
  const unprogrammedLiftIds = required.filter((id) => !programmed.has(id));
  const coverageVariant =
    unprogrammedLiftIds.length === 0
      ? LENS_FULL_COVERAGE_VARIANT[lens]
      : undefined;
  return { coverageVariant, unprogrammedLiftIds };
}

/** Function to compute lens lift coverage from scan.
 * @param {ReturnType<typeof import("./insightsWeekScan").scanWeekExercises>} scan
 * @param {InsightsPerLiftLens|string} lens
 */
export function computeLensLiftCoverageFromScan(scan, lens) {
  return computeLensLiftCoverageFromProgrammedLiftIds(
    scan.perLiftProgrammedLiftIdsByLens[lens] ?? new Set(),
    lens
  );
}

/** Function to compute lens lift coverage for a week.
 * @param {object[]} exercises
 * @param {InsightsPerLiftLens|string} lens
 * @param {(ex: object) => { totalSets: number }} accumulateExerciseSetAggregates
 */
export function computeLensLiftCoverageForWeek(
  exercises,
  lens,
  accumulateExerciseSetAggregates
) {
  const programmed = new Set();
  for (const ex of exercises) {
    const liftId = mapExerciseToLensLiftId(ex, lens);
    if (!liftId) {
      continue;
    }
    const agg = accumulateExerciseSetAggregates(ex);
    if (agg.totalSets > 0) {
      programmed.add(liftId);
    }
  }
  return computeLensLiftCoverageFromProgrammedLiftIds(programmed, lens);
}

/**
 * Per-lens footer row labels and volume tooltip keys (`volumeInfoKey`).
 * Lift balance copy in `InsightsLiftBalanceSection`.
 */
export const INSIGHTS_LENS_WEEK_SUMMARY_LABEL_KEYS = Object.freeze({
  powerlifting: Object.freeze({
    volumeLabelKey: "sbdVolumeLabel",
    volumeInfoKey: "sbdVolumeInfo",
  }),
  olympic_weightlifting: Object.freeze({
    volumeLabelKey: "olympicCombinedVolumeLabel",
    volumeInfoKey: "olympicCombinedVolumeInfo",
  }),
  strongman: Object.freeze({
    volumeLabelKey: "strongmanCombinedVolumeLabel",
    volumeInfoKey: "sbdVolumeInfo",
  }),
  streetlifting: Object.freeze({
    volumeLabelKey: "streetliftingCombinedVolumeLabel",
    volumeInfoKey: "sbdVolumeInfo",
  }),
});

/**
 * Footer under per-lift cards: metrics + label keys for `InsightsPowerliftingWeekSummary`.
 *
 * @param {InsightsPerLiftLens|string} lensId
 * @param {ReturnType<typeof import("./insightsWeekOverview").buildLensCompFooterPayload>|undefined} footer
 * @returns {object}
 */
export function buildLensWeekSummaryForPanel(lensId, footer) {
  const labels =
    INSIGHTS_LENS_WEEK_SUMMARY_LABEL_KEYS[lensId] ??
    INSIGHTS_LENS_WEEK_SUMMARY_LABEL_KEYS.powerlifting;
  return {
    ...labels,
    ...(footer ?? {
      volumeDisplay: "0",
      liftBalance: null,
      coverageVariant: undefined,
      unprogrammedLiftIds: [],
    }),
  };
}
